import React from 'react'
import Image from 'components/Artworks/Image/Basic'
import { getImageProps } from 'components/Artworks/Image/helpers'
import OriginalInfo from 'components/Artworks/Info/Original'
import { getInfoProps } from 'components/Artworks/Info/helpers'
import { algoliaEventsApi } from 'apis/algolia'
import { getDataLayerItemVariant } from 'lib/helpers'
import {
  DESKTOP_COLUMN_WIDTH,
  MOBILE_COLUMN_WIDTH,
  SPAN_HEIGHT,
} from 'components/Artworks/constants'
import { convertFromCentsToDollars } from 'components/FormatPrice/helpers'
import type { ConnectorProps as ArtworkProps } from '../../connectors/Artwork'
import ArtworkConnector from '../../connectors/Artwork'
import { getAlgoliaQueryIDFromLocalStorage } from 'lib/algolia/v2/helpers'
type Props = ArtworkProps & {
  algoliaIndice: string
  algoliaQueryId: string
  algoliaUserToken: string
  hasPrints: boolean
  index: number
  isMobile: boolean
  lazyLoad: boolean
  priority: boolean
}

const Component = (props: Props): React.ReactElement<React.ComponentProps<any>, any> | null => {
  const {
    algoliaIndice,
    algoliaQueryId,
    algoliaUserToken,
    artwork,
    isMobileTraffic,
    isMobile,
    lazyLoad,
    priority,
    title,
    category,
    userID,
  } = props
  const algoliaQueryIdFromLocalStorage = getAlgoliaQueryIDFromLocalStorage()

  if (!artwork) {
    return null
  }

  // PDPs to open in new tabs when in *Desktop* view (NON mobile devices)
  const shouldOpenNewDesktopPdpTab = !isMobileTraffic
  // We need to figure out Height/SPAN size of Image
  const {
    aspectRatio,
    artworkId,
    positionID,
    category: artworkCategory,
    sku,
    artworkTitle,
    firstName,
    lastName,
    listPrice,
  } = artwork
  const columnWidth = isMobile ? MOBILE_COLUMN_WIDTH : DESKTOP_COLUMN_WIDTH
  const imageHeight = parseInt(columnWidth / aspectRatio, 10)
  const imageSpanCount = Math.round(imageHeight / SPAN_HEIGHT)
  // Artwork info is a constant SPAN siz
  const infoSpanCount = isMobile ? 5 : 6
  const totalSpanCount = imageSpanCount + infoSpanCount
  // Helpers to PASS only props NEEDED
  const dataProps = {
    dataApi: 'algolia',
    dataList: 'feature-results',
    dataListId: 'feature-results',
    dataListName: `Results for '${title}' ${category}`,
  }
  const imageProps = getImageProps(props)
  const infoProps = getInfoProps(props)
  // Algolia Events
  const algoliaParams = {
    eventType: 'click',
    index: algoliaIndice,
    objectIDs: [artworkId],
    positions: [positionID],
    queryID: algoliaQueryId,
    userToken: algoliaUserToken,
  }
  const actionProps = {
    handleViewProduct: (ev: React.SyntheticEvent<Record<string, any>>) => {
      const { id, position, list, name } = ev.currentTarget.dataset
      // Delete this dataLayer event in July as part of universal analytics sunset
      id &&
        window.dataLayer.push({
          event: 'productClick',
          ecommerce: {
            click: {
              actionField: {
                list, // feature-results
              },
              products: [
                {
                  id,
                  // Product sku
                  position,
                  // Location in the results
                  name, // artwork title
                },
              ],
            },
          },
        })
      // This is the new dataLayer event for GA4
      // Clear the previous ecommerce object.
      window.dataLayer.push({
        ecommerce: null,
      })
      id &&
        window.dataLayer.push({
          event: 'select_item',
          ecommerce: {
            item_list_id: dataProps.dataListId,
            item_list_name: dataProps.dataListName,
            items: [
              {
                item_category: artworkCategory.replace(/s$/, '').replace(/\sM/, 'm'),
                item_id: sku || '',
                item_art_id: artworkId || '',
                item_name: artworkTitle || '',
                item_brand: `${firstName || ''} ${lastName || ''}` || '',
                item_variant: getDataLayerItemVariant(sku),
                index: positionID,
                item_list_id: dataProps.dataListId,
                item_list_name: dataProps.dataListName,
                affiliation: 'Online Store',
                price: convertFromCentsToDollars(listPrice) || 0,
                quantity: 1,
              },
            ],
          },
        })
      // Algolia event
      id && algoliaEventsApi({
        events: [
          {
            eventName: 'select_item',
            eventType: 'click',
            index: algoliaIndice,
            userToken: algoliaUserToken,
            authenticatedUserToken: String(userID) || '',
            objectIDs: [String(artworkId)],
            positions: [positionID],
            ...(algoliaQueryIdFromLocalStorage && {
              queryID: algoliaQueryIdFromLocalStorage,
            }), // pass queryID only if its present, otherwise do not pass
          },
        ],
      })
    },
  }
  return (
    <figure
      style={{
        gridRowEnd: `span ${totalSpanCount}`,
      }}
    >
      <Image
        shouldOpenNewDesktopPdpTab={shouldOpenNewDesktopPdpTab}
        {...imageProps}
        {...dataProps}
        lazyLoad={lazyLoad}
        onClick={actionProps.handleViewProduct}
        priority={priority}
        eventType='plp-feature'
        algoliaSearchIndex={algoliaIndice}
        algoliaUserToken={algoliaUserToken}
        algoliaQueryID={algoliaQueryIdFromLocalStorage}
        userID={userID}
      />

      <OriginalInfo
        {...actionProps}
        {...infoProps}
        shouldOpenNewDesktopPdpTab={shouldOpenNewDesktopPdpTab}
      />
    </figure>
  )
}

export default ArtworkConnector(Component)